import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import OfficeCaptureFields from '../OfficeCaptureFields';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomerEditPage = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [demoAppInstalled, setDemoAppInstalled] = useState(null);

    useEffect(() => {
        setLatitude(null);
        setLongitude(null);
        setPhotoFile(null);

        axios.get(`${BASE_URL}api/ProspCustomers/GetCustomerDetails?customerID=${customerId}`)
            .then(response => {
                setCustomer(response.data);
                setDemoAppInstalled(response.data?.bit1 ?? null);
            })
            .catch(() => {
                setError('Failed to load customer details.');
            });
    }, [customerId]);

    const handleChange = (field) => (e) => {
        setCustomer(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleLocationCaptured = (lat, lng) => {
        setLatitude(lat);
        setLongitude(lng);
    };

    const handleSave = () => {
        setSaving(true);
        setError(null);

        const formData = new FormData();
        formData.append('customerId', Number(customerId));
        formData.append('name', customer.vcmx2 || '');
        formData.append('company', customer.vcmx1 || '');
        formData.append('contact', customer.vcmx3 || '');
        formData.append('address', customer.vcmx4 || '');
        if (latitude != null) formData.append('Latitude', latitude);
        if (longitude != null) formData.append('Longitude', longitude);
        if (demoAppInstalled != null) formData.append('DemoAppInstalled', demoAppInstalled);
        if (photoFile) formData.append('Photo', photoFile);

        axios.post(`${BASE_URL}api/ProspCustomers/UpdateCustomer`, formData)
            .then(() => {
                navigate(`/customer-view/${customerId}`);
            })
            .catch(() => {
                setError('Failed to save customer. Please try again.');
                setSaving(false);
            });
    };

    if (error && !customer) {
        return (
            <div className="container mt-5 text-center">
                <div className="alert alert-danger" role="alert">{error}</div>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p>Loading customer details...</p>
            </div>
        );
    }

    return (
        <div className="container mt-5" style={{ maxWidth: '600px' }}>
            <div className="card shadow-sm">
                <div className="card-header bg-primary text-white">
                    <h4 className="m-0"><i className="bi bi-pencil-square me-2"></i>Edit Customer</h4>
                </div>
                <div className="card-body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="mb-3">
                        <label htmlFor="name" className="form-label">Name</label>
                        <input
                            type="text"
                            className="form-control"
                            id="name"
                            value={customer.vcmx2 || ''}
                            onChange={handleChange('vcmx2')}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="company" className="form-label">Company</label>
                        <input
                            type="text"
                            className="form-control"
                            id="company"
                            value={customer.vcmx1 || ''}
                            onChange={handleChange('vcmx1')}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="contact" className="form-label">Contact</label>
                        <input
                            type="text"
                            className="form-control"
                            id="contact"
                            value={customer.vcmx3 || ''}
                            onChange={handleChange('vcmx3')}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="address" className="form-label">Address</label>
                        <textarea
                            className="form-control"
                            id="address"
                            rows="2"
                            value={customer.vcmx4 || ''}
                            onChange={handleChange('vcmx4')}
                        />
                    </div>
                    <OfficeCaptureFields
                        key={customerId}
                        demoAppInstalled={demoAppInstalled}
                        onDemoAppInstalledChange={setDemoAppInstalled}
                        onPhotoSelected={setPhotoFile}
                        onLocationCaptured={handleLocationCaptured}
                        existingPhotoUrl={customer.vcmx5 ? `${BASE_URL}${customer.vcmx5}` : null}
                        existingLatitude={customer.flt1}
                        existingLongitude={customer.flt2}
                    />
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(`/customer-view/${customerId}`)}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerEditPage;
